import { ComponentPropsWithoutRef, FC, HTMLAttributes, ReactNode } from 'react'
import styled from 'styled-components'

import { Carat, TypeStyle, Warning, fromMd } from '@syconium/little-miss-figgy'

import { EmbroideryFormKind } from '../../../../containers/EmbroideryService/types'
/**
 * Embroidery option selector list wrapper.
 */
const List: FC<HTMLAttributes<HTMLUListElement>> = ({ children, ...rest }) => {
  return <ul {...rest}>{children}</ul>
}

/**
 * Embroidery option selector list item.
 *
 *
 * @param props
 * @param props.index - The index of the given option.
 * @param props.handleClick - Callback function for when the option is clicked.
 */
type EmbroideryOptionProps = {
  index: number
  disabled?: boolean
  selected: boolean
  kind: EmbroideryFormKind
  handleClick: (kind: EmbroideryFormKind) => void
  label?: string
  error?: ReactNode
  errorMessageId?: string
} & HTMLAttributes<HTMLLIElement>

const ListOption: FC<EmbroideryOptionProps> = ({
  children,
  index,
  kind,
  label,
  handleClick,
  disabled,
  selected,
  error,
  errorMessageId,
}) => {
  return (
    <li key={index}>
      <ListButton
        disabled={disabled}
        aria-label={`${label}`}
        aria-describedby={errorMessageId}
        aria-invalid={Boolean(error)}
        onClick={() => handleClick(kind)}
        $selected={selected}
      >
        <ListItemContent>{children}</ListItemContent>
        {error && <ListItemError>{error}</ListItemError>}
      </ListButton>
    </li>
  )
}

/**
 * The right-most content on the embroidery option.
 */
const RightContent: FC<{ $disabled?: boolean; children: ReactNode }> = ({
  children,
  $disabled,
}) => {
  return <RightContentWrap $disabled={$disabled}>{children}</RightContentWrap>
}

const Icon: FC<{}> = () => {
  return (
    <IconWrap>
      <Carat />
    </IconWrap>
  )
}

/**
 * An error message that should be displayed alongside an invalid embroidery option.
 */
const Error: FC<
  { message?: string; id: string } & Omit<ComponentPropsWithoutRef<typeof ErrorMessage>, 'children'>
> = ({ id, message, ...rest }) => {
  return (
    <ErrorMessage id={id} {...rest}>
      <ErrorIcon width='16px' height='16px' /> {message}
    </ErrorMessage>
  )
}

export const EmbroideryOptionComponents = {
  Error,
  List,
  RightContent,
  Icon,
  ListOption,
}

const ErrorMessage = styled(TypeStyle.BodyNano).attrs({ asTag: 'span' })`
  font-weight: 600;
  color: ${o => o.theme.color.stroke.critical.text.on.background};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`

const ErrorIcon = styled(Warning).attrs(o => ({ fill: o.theme.color.fill.critical.subtle }))``

const ListButton = styled.button<{ $selected: boolean }>`
  ${TypeStyle.css.headlineMicro}
  width: 100%;
  padding: ${o => o.theme.spacing(5)} 0;
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};

  ${fromMd} {
    padding: ${o => o.theme.spacing(5)} 0;
  }

  &:hover {
    cursor: ${o => o.$selected && 'default'};
  }

  &:disabled {
    cursor: not-allowed;
  }

  li:first-child & {
    border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  }
`

const ListItemContent = styled.div`
  display: flex;
  justify-content: space-between;
`
const ListItemError = styled.div`
  margin-top: ${o => o.theme.spacing(2)};
`

const IconWrap = styled.div`
  padding-left: ${o => o.theme.spacing(2)};
`
const RightContentWrap = styled.div<{ $disabled?: boolean }>`
  ${TypeStyle.css.headlineMicro}
  color: ${o =>
    o.$disabled
      ? o.theme.color.stroke.disabled.text.on.background
      : o.theme.color.text.primary.on.background}
  font-weight: ${o => o.theme.typography.font.weight[700]};
  display: flex;
  align-items: center;
`
